import {
  Component,
  EventEmitter,
  forwardRef,
  HostBinding,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { ReactiveComponent } from '@app/shared/components/reactive-component';
import { FadeInOutDelayed } from '@app/utils/angular-animations';

const SWITCH_TOGGLE_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => SwitchToggleComponent),
  multi: true,
};

type ToggleStyle = 'x-small' | 'small' | 'large' | 'wide' | 'simple';

@Component({
  selector: 'portal-switch-toggle',
  templateUrl: './switch-toggle.component.html',
  providers: [SWITCH_TOGGLE_VALUE_ACCESSOR],
  animations: [FadeInOutDelayed],
  host: {
    class: 'portal-switch-toggle',
  },
  standalone: false,
})
export class SwitchToggleComponent extends ReactiveComponent implements OnChanges, OnInit {
  @Input()
  id: string = '';

  @Input()
  disabled: boolean = false;

  @Input()
  labelActive: string = 'switch.on';

  @Input()
  labelInactive: string = 'switch.off';

  @Input()
  pending: boolean = false;

  @Input()
  size?: ToggleStyle;

  @Input()
  value: boolean = false;

  @Input()
  preventAndStop: boolean = false;

  @Output()
  onValueChanged: EventEmitter<boolean> = new EventEmitter();

  @HostBinding('class')
  className: string = '';

  isChecked: boolean = false;

  public get isDisabled(): boolean {
    return this.disabled || this.pending;
  }

  onClick($event: MouseEvent): void {
    if (this.preventAndStop !== false) {
      // $event.preventDefault();
      $event.stopPropagation();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('value' in changes) {
      this.isChecked = changes.value.currentValue;
    }

    setTimeout(() => this.updateClassName(), 0);
  }

  ngOnInit() {
    this.isChecked = this.value;

    this.updateClassName();
  }

  onCheckChange(eventTarget: EventTarget) {
    const target = eventTarget as HTMLInputElement;

    this.onValueChange(target.checked);
    this.updateClassName();
  }

  onValueChange(value: boolean) {
    if (this.isDisabled === false) {
      this.onChange(value);
      this.onValueChanged.emit(value);

      this.isChecked = value;

      if (!this.touched) {
        this.touched = true;
        this.onTouched();
      }
    }
  }

  setDisabledState(isDisabled: boolean): void {
    super.setDisabledState(isDisabled);

    this.updateClassName();
  }

  writeValue(obj: boolean): void {
    this.isChecked = obj;

    this.updateClassName();
  }

  private updateClassName(): void {
    const classes: string[] = [];

    if (this.isChecked) {
      classes.push('portal-switch-toggle--on');
    }

    if (this.isDisabled) {
      classes.push('portal-switch-toggle--disabled');
    }

    classes.push(`portal-switch-toggle--${this.size}`);

    this.className = classes.join(' ').trim();
  }
}
